import { ChevronDownIcon } from '@moia-dev/pace-icons'
import type { TextFieldProps } from '@mui/material'
import { useCallback } from 'react'
import type { FieldRenderProps } from 'react-final-form'
import { MoiaTextField } from '../MoiaTextField/MoiaTextField'
import { getFinalFormError } from './FormControlErrorHandler'

const textFieldStyle = {
  display: 'flex',
}

export type MoiaFormTextFieldProps<Value = unknown, InputValue = Value> = {
  /**
   * Feature flag to migrate legacy in line styles api
   */
  legacyStyles?: boolean

  translateValidation?: boolean
} & TextFieldProps &
  FieldRenderProps<Value, HTMLElement, InputValue>

/**
 * Wrapper component to be used with FinalForm
 *
 * @deprecated Use react-hook-form instead. See our forms documentation in our pattern-library or our docs.
 */
export function MoiaFormTextField<Value = unknown, InputValue = Value>({
  input,
  InputLabelProps,
  inputProps,
  InputProps,
  label,
  meta,
  type,
  helperText,
  classes,
  legacyStyles = true,
  fullWidth,
  variant = 'standard',
  disabled,
  ...restProps
}: MoiaFormTextFieldProps<Value, InputValue>) {
  const { error, errorHelperText } = getFinalFormError(meta)
  const style = legacyStyles ? textFieldStyle : undefined
  const inputLabelProps =
    type === 'date' ? { ...InputLabelProps, shrink: true } : InputLabelProps

  const Icon = useCallback(
    () => (
      <ChevronDownIcon
        css={{
          stroke: disabled
            ? 'var(--color-content-disabled)'
            : 'var(--color-content-default)',
        }}
      />
    ),
    [disabled],
  )

  return (
    <MoiaTextField
      classes={classes}
      style={style}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth ?? !legacyStyles}
      helperText={errorHelperText ?? helperText}
      id={input.name}
      InputLabelProps={inputLabelProps}
      inputProps={inputProps}
      // Thanks, MUI! (https://material-ui.com/api/text-field/#props)
      InputProps={InputProps}
      label={label}
      SelectProps={{ IconComponent: Icon }}
      type={type}
      variant={variant}
      {...input}
      {...{ ...restProps }}
    />
  )
}
